
<template lang="pug">
    v-card.mx-lg-auto(width="100%")
        v-toolbar.volie-v-toolbar-color(dark)
            v-toolbar-title Update Appointment Status

        v-card-text
            v-container

                v-row
                    v-col(cols='12')
                        v-select(
                            v-model="formStatus"
                            label="Status"
                            :items="statuses"
                            item-text="name"
                            item-value="value"
                        )

                        template(v-if="formStatus === 'rescheduled' && this.appointment.status !== 'rescheduled'")

                            v-menu(
                                v-model='datePickerMenu'
                                :close-on-content-click='false'
                                transition='scale-transition'
                                offset-y
                                max-width='290px'
                                min-width='290px'
                            )

                                template(v-slot:activator='{ on, attrs }')
                                    v-text-field(
                                        v-model='formatAppointmentDate'
                                        label='Date *'
                                        prepend-icon='mdi-calendar'
                                        readonly
                                        v-bind='attrs'
                                        v-on='on'

                                    )
                                v-date-picker(
                                    v-model='appointmentDatetimeDate'
                                    no-title
                                    @input='datePickerMenu = false'
                                    :min="minDate"
                                )

                            Timepicker(
                                v-if="accountMinutes === 'anytime'"
                                label="Time *"
                                :on-change="onChangeTimepicker"
                                :value="appointmentDatetimeTime"
                                :appointment-scheduling-hour="appointmentSchedulingHour"
                            )

                            AppointmentTimepicker(
                                v-else 
                                label="Time *"
                                :on-change="onChangeTimepicker"
                                :value="appointmentDatetimeTime"
                                :accountMinutes="accountMinutes"
                                :appointment-scheduling-hour="appointmentSchedulingHour"
                            )

                            v-select(
                                v-model='timeZoneId'
                                :items='currentUser.time_zones'
                                label='Time Zone *'
                                item-text="name"
                                item-value="id"
                            )

                        v-select(
                            v-model="appointmentTypeId"
                            label="Type"
                            :disabled="!validAppiontmentTypes"
                            :items="appointmentTypes"
                            item-text="name"
                            item-value="appointment_type_id"
                        )

                        v-select(
                            v-if="!hideAgentSelection"
                            v-model='userId'
                            :items='users'
                            label='Agent'
                            item-text="user_full_name"
                            item-value="user_id"
                            clearable
                        )

                        v-text-field(
                            v-if="renderRecallFields"
                            v-model="declineBonuses"
                            label='Decline Bonuses'
                            type='number'
                            min='0'
                        )

                        v-text-field(
                            v-if="renderRecallFields"
                            v-model="recallBonuses"
                            label='Recall Bonuses'
                            type='number'
                            min='0'
                        )

                        v-textarea(
                            v-model="specialInstructions"
                            label='Special Instructions'
                            rows="1"
                            auto-grow
                            clearable
                            counter
                            outlined
                            dense
                            prepend-inner-icon="mdi-text"
                        )

                v-row
                    v-col(cols='12')
                        .text-right
                            v-btn(v-if="closeDialog" outlined color="primary" @click="closeDialog()")
                                v-icon mdi-window-close
                                | Cancel

                            v-btn.ma-2.white--text(:disabled="saveButtonDisabled()" ripple color="green" v-if="appointment.appointment_id > 0" :loading="isLoading" @click="updateAppointment()")
                                v-icon mdi-check
                                | Update Appointment
</template>

<script lang="ts">
import Vue from 'vue';
import VolieState  from "@/store";

    // Components
import Timepicker from "@/components/shared/timepicker.vue";
import AppointmentTimepicker from "@/components/shared/appointments/timepicker.vue";

// Data
import { GetAppointmentTypes, GetOrganizationOrganizationUsers, UpdateAppointment } from '@/data';

export default Vue.extend({
    data() {
        return {
            appointmentTypes: [],
            appointmentDatetimeDate: null,
            appointmentDatetimeTime: null,
            datePickerMenu: false,
            formStatus: null,
            declineBonuses: '0',
            isLoading: false,
            recallBonuses: '0',
            appointmentTypeId: null,
            specialInstructions: null,
            statuses: [],
            timeZoneId: null,
            userId: null,
            users: [],
        }
    },
    methods: {
        formatDate (date) {
            if (!date) {
                return null;
            }

            const [year, month, day] = date.split('-');
            return `${month}/${day}/${year}`;
        },
        getAppointmentTypes() {
            this.isLoading = true;
            GetAppointmentTypes({}).then((response) => {
                if (!!response.Data && !!response.Data.appointment_types && response.Data.appointment_types.length > 0) {
                    this.appointmentTypes = response.Data.appointment_types;
                }
                this.isLoading = false;
            });
        },
        getOrganizationOrganizationUsers() {
            if (!this.appointment.organization_id) {
                return;
            }

            GetOrganizationOrganizationUsers(this.appointment.organization_id.toString()).then((response) => {
                if (response?.Data?.organization_organization_users?.length > 0) {
                    this.users = response.Data.organization_organization_users;
                }
            }).catch((error) => {
                console.error('Error fetching organization users:', error);
            });
        },
        init() {
            this.setStatuses();
            this.setMinDate();
            this.setDefaultTimeZone();
            this.appointmentTypeId = this.appointment.appointment_type_id ?? null;
            this.formStatus = this.appointment.status ?? null;
            this.declineBonuses = ((this.appointment.decline_bonuses || 0) ?? '0').toString();
            this.recallBonuses = ((this.appointment.recall_bonuses || 0) ?? '0').toString();
            this.specialInstructions = this.appointment.special_instructions ?? null;
            this.userId = this.appointment.user_id ?? null;
        },
        onChangeTimepicker(time) {
            this.appointmentDatetimeTime = time;
        },
        saveButtonDisabled() {
            let isDisabled = true

            if (this.declineBonuses?.length > 0) {
                isDisabled = false
            }

            if (this.recallBonuses?.length > 0) {
                isDisabled = false
            }

            if (this.specialInstructions?.length > 0) {
                isDisabled = false
            }

            if (this.formStatus !== this.appointment.form_status) {
                isDisabled = false
            }

            if (this.appointmentTypeId !== this.appointment.appointment_type_id) {
                isDisabled = false
            }

            if (this.formStatus === 'rescheduled' && this.formStatus !== this.appointment.form_status) {
                if (this.validFutureAppointment) {
                    isDisabled = false
                } else {
                    isDisabled = true
                }
            }

            return isDisabled

        },
        setDefaultTimeZone() {
            // If still invalid, default to eastern
            if (!(this.timeZoneId > 0)) {
                this.timeZoneId = 43;
            }
        },
        setMinDate() {
            const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
            this.minDate = (new Date(Date.now() - tzoffset)).toISOString().slice(0, -1);
        },
        setStatuses() {

            this.statuses = [
                {
                    name: "Canceled",
                    value: "canceled"
                },
                {
                    name: "Confirmed",
                    value: "confirmed"
                },
                {
                    name: "Duplicate",
                    value: "duplicate"
                },
                {
                    name: "No Show",
                    value: "no_show"
                },
                {
                    name: "Rescheduled",
                    value: "rescheduled"
                },
                {
                    name: "Set",
                    value: "set"
                },
                {
                    name: "Show",
                    value: "show"
                },
                {
                    name: "Sold",
                    value: "sold"
                }
            ]

            if (this.currentUserIsAgent) {
                this.statuses = this.statuses.filter(status => ['canceled', 'confirmed', 'rescheduled', 'set'].includes(status.value));
            }
        },
        updateAppointment() {
            if (!this.formStatus) {
                return;
            }
            if (this.appointment?.appointment_key?.length === 0) {
                return;
            }

            let statusToUpdate = this.appointment.status;

            if (this.appointment.status !== this.formStatus) {
                statusToUpdate = this.formStatus;
            }

            let appointmentDatetimeDate = '';
            let appointmentDatetimeTime = '';

            if (this.formStatus === 'rescheduled' && this.appointment.status !== 'rescheduled') {
                appointmentDatetimeDate = this.appointmentDatetimeDate;
                appointmentDatetimeTime = this.appointmentDatetimeTime;
            }

            const options = {
                appointment_id: this.appointment?.appointment_id,
                appointment_type_id: this.appointmentTypeId,
                appointment_datetime_date: appointmentDatetimeDate,
                appointment_datetime_time: appointmentDatetimeTime,
                decline_bonuses: this.declineBonuses,
                recall_bonuses: this.recallBonuses,
                special_instructions: this.specialInstructions,
                status: statusToUpdate,
                time_zone_id: this.timeZoneId || 0,
                user_id: this.userId,
            }

            this.isLoading = true;
            UpdateAppointment(this.appointment.appointment_key, options).then((response) => {
                if (response?.Data?.appointment?.appointment_id > 0) {
                    this.showSnackbar("success", "Successfully updated appointment");
                    this.$emit('recall-bonus-added', this.recallBonuses)
                    this.$emit('decline-bonus-added', this.declineBonuses)
                    this.$emit('appointment-updated', response.Data.appointment, response.Data.appointment.status)
                } else {
                    this.showSnackbar("error", response.Error);
                }

                this.isLoading = false;
            });
        }
    },
    computed: {
        accountMinutes() {
            return this.currentUser?.account?.appt_times_setting;
        },
        currentUser() {
            return VolieState.currentUser();
        },
        currentUserIsAgent() {
            return this.currentUser?.activated_role_id === 3 || this.currentUser?.activated_role_id === 9;
        },
        formatAppointmentDate() {
            return this.formatDate(this.appointmentDatetimeDate);
        },
        renderRecallFields() {
            return this.currentUser?.account?.account_id === 1020;
        },
        validFutureAppointment() {
            if (this.formStatus !== 'rescheduled') {
                return true;
            }

            if (this.formStatus === 'rescheduled') {
                const now = new Date();

            // Combine the date and time strings
            const dateTimeString = `${this.appointmentDatetimeDate}T${this.appointmentDatetimeTime}:00`;

            // Convert the combined string to a Date object
            const dateTimeCheckAppointment = new Date(dateTimeString);

                return dateTimeCheckAppointment > now;
            }
            return true;
        },
        validAppiontmentTypes() {
            return this.appointmentTypes.length > 0;
        },
    },
    components: {
        AppointmentTimepicker,
        Timepicker,
    },
    props: {
        hideAgentSelection: {
            type: Boolean,
            required: false,
            default: false,
        },
        appointment: {
            type: Object,
            required: true
        },
        appointmentSchedulingHour: {
            type: Object,
            required: false,
            default: null,
        },
        closeDialog: {
            type: Function,
            required: false,
            default: null,
        },
        showSnackbar: Function,
    },
    watch: {
        "appointment.appointment_id": "init",
        "appointment.status": "init",
    },
    created() {
        this.getAppointmentTypes();
        this.getOrganizationOrganizationUsers();

        // Set date so it is pre-populated to previous
        this.appointmentDatetimeTime = this.appointment.appointment_entered_time + ' ' + this.appointment.appointment_am_pm;
        const datePieces = this.appointment.appointment_datetime.split("T");
        if (datePieces?.length > 0) {
            this.appointmentDatetimeDate = datePieces[0];
        }
    },
    destroyed() {
    },
    mounted() {
        this.init();
    },
})
</script>

<style lang="scss" scoped>

</style>
